import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

/** POST api/{invites/accept,onboarding} keycloakDetails block. Nulled in both flows here
 *  because the user already has a Keycloak login (they're authenticated), so there is no
 *  password to set. */
export interface KeycloakDetails {
  password: string;
  passwordIsTemporary: boolean;
  sendVerifyEmail: boolean;
}

/** Request body for POST api/invites/accept. */
export interface AcceptInviteRequest {
  code: string;
  accountName: string | null;
  firstName: string;
  lastName: string;
  email: string;
  username: string | null;
  keycloakDetails: KeycloakDetails | null;
  sendVerifyEmail: boolean;
}

/** Request body for POST api/onboarding (creates a brand-new realm/account). */
export interface OnboardingRequest {
  accountName: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string | null;
  keycloakDetails: KeycloakDetails | null;
  sendVerifyEmail: boolean;
}

/**
 * Self-service account recovery for an authenticated Keycloak user who has no Vigilant
 * realm/user yet (GET api/users/self → 403). Both calls derive identity (name/email/
 * username) from the JWT claims and send `keycloakDetails: null` since the login already
 * exists. These two endpoints are explicitly allowed past the account gate (see
 * accountGateInterceptor) so they work while every other API call is halted.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  /** Name/email/username pulled from the Keycloak JWT for the request bodies. */
  private identity(): Pick<OnboardingRequest, 'firstName' | 'lastName' | 'email' | 'username'> {
    const p = this.auth.userProfile();
    return {
      firstName: p?.given_name ?? '',
      lastName: p?.family_name ?? '',
      email: p?.email ?? '',
      username: p?.preferred_username ?? null,
    };
  }

  /**
   * Join an existing account with an invite code. The operator may edit their first/last name;
   * email + username still come from the JWT. The account already exists, so accountName is left
   * null. The user already logged in via Keycloak, so keycloakDetails is null; we still ask the
   * server to send a confirmation email.
   */
  acceptInvite(input: { code: string; firstName: string; lastName: string }): Observable<unknown> {
    const id = this.identity();
    const body: AcceptInviteRequest = {
      code: input.code.trim(),
      accountName: null,
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email: id.email,
      username: id.username,
      keycloakDetails: null,
      sendVerifyEmail: true,
    };
    return this.http.post(`${environment.vigilantApiUrl}/api/invites/accept`, body);
  }

  /**
   * Create a brand-new realm/account. The operator may edit their first/last name and the account
   * name; email + username still come from the JWT. The user already logged in via Keycloak, so
   * keycloakDetails is null; the confirmation email is opt-in via `sendVerifyEmail`.
   */
  createAccount(input: {
    firstName: string;
    lastName: string;
    accountName: string;
    sendVerifyEmail: boolean;
  }): Observable<unknown> {
    const id = this.identity();
    const body: OnboardingRequest = {
      accountName: input.accountName.trim(),
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email: id.email,
      username: id.username,
      keycloakDetails: null,
      sendVerifyEmail: input.sendVerifyEmail,
    };
    return this.http.post(`${environment.vigilantApiUrl}/api/onboarding`, body);
  }

  /**
   * Public self-service signup from the login page — the user is NOT authenticated yet, so identity
   * and a Keycloak password are collected here (not derived from a JWT) and sent as keycloakDetails.
   * Otherwise identical to {@link createAccount}. The new login is permanent (not temporary); the
   * confirmation email is opt-in.
   */
  register(input: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    accountName: string;
    sendVerifyEmail: boolean;
  }): Observable<unknown> {
    const email = input.email.trim();
    const body: OnboardingRequest = {
      accountName: input.accountName.trim(),
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email,
      username: email,
      keycloakDetails: {
        password: input.password,
        passwordIsTemporary: false,
        sendVerifyEmail: input.sendVerifyEmail,
      },
      sendVerifyEmail: input.sendVerifyEmail,
    };
    return this.http.post(`${environment.vigilantApiUrl}/api/onboarding`, body);
  }
}
